import axios from 'axios';
import { useRecorderStore } from '../stores/recorderStore';
import { useUserStore } from '../stores/userStore';

// 使用相对路径 /api，通过代理访问后端
// 开发环境由 setupProxy.js 代理到后端（端口取 BACKEND_PORT，默认 7586，测试实例 7593）
const API_BASE_URL = '/api';

// 把后端下发的续期令牌写回 store（persist 中间件会同步到 localStorage）
export const applyRefreshedToken = (token: unknown, requestToken: string | null): void => {
  if (typeof token !== 'string' || !token) return;
  const current = useUserStore.getState().token;
  if (!requestToken || current !== requestToken || current === token) return;
  useUserStore.getState().setToken(token);
  console.log('[认证] 登录凭证已自动续期');
};

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 token
client.interceptors.request.use(
  (config) => {
    // 从 zustand persist 存储中获取 token
    const userStorage = localStorage.getItem('user-storage');
    if (userStorage) {
      try {
        const parsed = JSON.parse(userStorage);
        const token = parsed?.state?.token;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (e) {
        console.error('Failed to parse user storage:', e);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误和解包响应
client.interceptors.response.use(
  (response) => {
    // 后端在凭证临近过期时会下发新令牌，写回本地存储实现滑动续期
    const authorization = response.config.headers?.Authorization;
    const requestToken = typeof authorization === 'string' ? authorization.replace(/^Bearer\s+/i, '') : null;
    applyRefreshedToken(response.headers?.['x-refreshed-token'], requestToken);

    const res = response.data;
    // 如果后端返回 code: 200，直接返回 data 字段
    if (res && res.code === 200) {
      return { ...response, data: res.data };
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const authorization = error.config?.headers?.Authorization;
      const requestToken = typeof authorization === 'string' ? authorization.replace(/^Bearer\s+/i, '') : null;
      if (!requestToken || requestToken !== useUserStore.getState().token) return Promise.reject(error);
      // 🔴 录音进行中绝不跳转：跳转会触发 pagehide/beforeunload 里的 abortRecording()，
      // 内存中尚未上传的录音会被直接清空（曾导致一场面试丢失约 17.5 分钟录音）。
      // 此时只标记凭证失效，由录音告警 UI 提示用户导出音频，凭证问题交由用户自行处理。
      const recorder = useRecorderStore.getState();
      if (recorder.isRecording || recorder.uploadingChunk || recorder.hasUnsavedAudio()) {
        recorder.markAuthExpired();
        return Promise.reject(error);
      }

      // 清除 zustand persist 存储
      localStorage.removeItem('user-storage');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default client;
